import React from 'react';
import AppListToolbarGlobal, { type CompanyOption } from '@/__core/components/AppLisstToolbarGlobal';
import {
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import Badge from '@mui/material/Badge';
import { sharedToolbarListStyles } from '@/__core/styles';

export interface StoresToolbarGlobalProps {
  /** @deprecated Chỉ dùng khi test; mặc định lấy từ auth + resolveModuleAccessibleCompanies */
  companies?: CompanyOption[];
  fetchCompanies?: () => Promise<CompanyOption[]>;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  onSearchSubmit?: () => void;
  onToggleAdvancedSearch?: () => void;
  isAdvancedSearchOpen?: boolean;
  onFilterChange?: (field: string, value: string) => void;

  filters?: {
    company: string;
    status: string;
  };
  statusOptions?: Array<{ id: string; name: string }>;
  selectedCount?: number;
  onDelete?: () => void;
  onRefresh?: () => void;
  canDelete?: boolean;
}

export default function StoresToolbarGlobal({
  companies,
  searchQuery = '',
  onSearchChange,
  onSearchSubmit,
  onToggleAdvancedSearch,
  isAdvancedSearchOpen = false,
  filters = { company: 'all', status: 'all' },
  onFilterChange,
  statusOptions = [],
  selectedCount = 0,
  onDelete,
  onRefresh,
  canDelete = false,
}: StoresToolbarGlobalProps) {
  const statusFilterOptions = React.useMemo(
    () => statusOptions.map((option) => ({ value: option.id, label: option.name })),
    [statusOptions]
  );

  const advancedFilterFields = React.useMemo(
    () => [
      {
        key: 'status',
        label: 'Trạng thái',
        labelWidth: 90,
        value: filters.status,
        placeholder: 'Chọn trạng thái',
        options: statusFilterOptions,
        onChange: (value: string) => onFilterChange?.('status', value),
        gridSize: { xs: 12, sm: 6, md: 6 },
      },
    ],
    [filters.status, onFilterChange, statusFilterOptions]
  );

  const rightActions = (
    <>
      {selectedCount > 0 && canDelete && onDelete && (
        <Tooltip title={`Xóa (${selectedCount})`} arrow>
          <IconButton
            onClick={onDelete}
            size='small'
            sx={{
              ...sharedToolbarListStyles.actionIconButtonBase,
              ...sharedToolbarListStyles.deleteIconButton,
            }}
          >
            <Badge badgeContent={selectedCount} color='error' max={99}>
              <DeleteIcon sx={sharedToolbarListStyles.toolbarActionIcon} />
            </Badge>
          </IconButton>
        </Tooltip>
      )}
      {onRefresh && (
        <Tooltip title='Làm mới' arrow>
          <IconButton
            onClick={onRefresh}
            size='small'
            sx={{
              ...sharedToolbarListStyles.actionIconButtonBase,
              backgroundColor: '#10b981',
              borderColor: '#10b981',
              color: 'white',
              '&:hover': { backgroundColor: '#059669', borderColor: '#059669' },
            }}
          >
            <RefreshIcon sx={sharedToolbarListStyles.toolbarActionIcon} />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  return (
    <AppListToolbarGlobal
      scopeModule='stores'
      companies={companies}
      searchPlaceholder='Tìm theo mã kho, tên kho, địa chỉ...'
      searchQuery={searchQuery}
      onSearchChange={onSearchChange}
      onSearchSubmit={onSearchSubmit}
      isAdvancedSearchOpen={isAdvancedSearchOpen}
      onToggleAdvancedSearch={onToggleAdvancedSearch}
      selectedCompany={filters.company}
      onCompanyChange={(id) => onFilterChange?.('company', id)}
      advancedFilterFields={advancedFilterFields}
      rightActions={rightActions}
    />
  );
}
